import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import clsx from 'clsx';
import { FC, ReactNode, useCallback, useRef } from 'react';
import TextInput from 'src/components/TextInput';
import { useSuppressBrowserAutofill } from './useSuppressBrowserAutofill';
import { EMPTY_SUPPLIER_DISPLAY_VALUE, toSupplierFieldValue } from './utils/supplierFormValues';

export const SUPPLIER_FORM_TOOLTIP_ID = 'supplier-form-tooltip';

/** Unrecognized token To OverRide Browser Autofill. */
export const SUPPLIER_FORM_AUTOCOMPLETE_OFF = 'nope';

interface SupplierFormFieldProps {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  readOnly?: boolean;
  className?: string;
  maxLength?: number;
  inputClassName?: string;
  helperText?: ReactNode;
  helperClassName?: string;
  trailingContent?: ReactNode;
  emptyPlaceholder?: string;
  showValidationErrorBorder?: boolean;
}

const SupplierFormField: FC<SupplierFormFieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  readOnly = false,
  className,
  maxLength,
  inputClassName,
  helperText,
  helperClassName,
  trailingContent,
  emptyPlaceholder,
  showValidationErrorBorder = false,
}) => {
  const inputValue = value ?? '';
  const displayValue = toSupplierFieldValue(value) || (emptyPlaceholder ?? EMPTY_SUPPLIER_DISPLAY_VALUE);
  const { autofillReadOnly, unlockAutofill, relockAutofill } = useSuppressBrowserAutofill();

  const handleChange = useCallback(
    (event: TextBoxChangeEvent) => {
      onChange(String(event.value ?? ''));
    },
    [onChange]
  );

  const lastBlurAtRef = useRef(0);

  const handleBlur = useCallback(() => {
    relockAutofill();
    const now = Date.now();
    if (now - lastBlurAtRef.current < 100) {
      return;
    }
    lastBlurAtRef.current = now;
    onBlur?.();
  }, [onBlur, relockAutofill]);

  return (
    <div className={clsx('flex min-w-0 flex-col gap-1', className)}>
      <label
        className="flex items-center gap-1 text-sm text-gray-500"
        htmlFor={readOnly ? undefined : id}
      >
        <span>
          {label}
          {required && !readOnly && <span className="text-error ml-0.5">*</span>}
        </span>
        {trailingContent}
      </label>
      {readOnly ? (
        <span
          data-tooltip-id={SUPPLIER_FORM_TOOLTIP_ID}
          data-tooltip-content={displayValue}
          data-tooltip-place="bottom"
          className="supplier-form__field-value block min-w-0 truncate"
        >
          {displayValue}
        </span>
      ) : (
        <TextInput
          id={id}
          autoComplete={SUPPLIER_FORM_AUTOCOMPLETE_OFF}
          name={`supplier-no-autofill-${id}`}
          readOnly={autofillReadOnly}
          onFocus={unlockAutofill}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          {...(maxLength != null ? { maxLength } : {})}
          valid={!error && !showValidationErrorBorder}
          className={clsx(
            error && '[&_.k-input-inner]:!border-2 [&_.k-input-inner]:!border-error',
            inputClassName
          )}
        />
      )}
      {helperText && <div className={clsx('text-xs', helperClassName)}>{helperText}</div>}
      {error && <Error id={`${id}-error`}>{error}</Error>}
    </div>
  );
};

export default SupplierFormField;
